import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { XIcon } from '@/components/icons';
import { cartService } from '@/features/cart/services/cartService';
import { GuestCheckoutErrorModal } from './GuestCheckoutErrorModal';
import { FormInput } from '@/features/user/components/signup/FormInput';
import type { GuestCheckoutFormData } from '@/features/cart/types';

interface GuestCheckoutConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  cartId: string;
  guestData: GuestCheckoutFormData;
  onSuccess: () => void;
}

export const GuestCheckoutConfirmationModal: React.FC<GuestCheckoutConfirmationModalProps> = ({
  isOpen,
  onClose,
  cartId,
  guestData,
  onSuccess,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState<string>('');
  const [confirmationError, setConfirmationError] = useState<string | undefined>();

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const handleConfirm = async () => {
    // Validate confirmation number
    if (!confirmationNumber || confirmationNumber.length < 4) {
      setConfirmationError('Please enter a valid confirmation number');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setConfirmationError(undefined);

    try {
      // Call guest checkout API
      await cartService.checkoutCartAsGuest(cartId, {
        email: guestData.email,
        first_name: guestData.firstName,
        last_name: guestData.lastName,
      });

      // Close modal and trigger success callback
      onClose();
      onSuccess();
    } catch (error) {
      console.error('Error during guest checkout:', error);
      const errorMessage = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
      setError(errorMessage);
      setShowErrorModal(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmationNumberChange = (value: string) => {
    // Only allow numbers, limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setConfirmationNumber(numericValue);
    if (confirmationError) {
      setConfirmationError(undefined);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/90 backdrop-blur-md"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="relative bg-gray-950 border border-gray-800/50 rounded-2xl p-8 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 bg-gray-800/80 hover:bg-gray-700/80 rounded-full text-gray-300 hover:text-white transition-all duration-200 backdrop-blur-sm"
            >
              <XIcon className="w-5 h-5" />
            </button>

            {/* Header Section */}
            <motion.div
              className="text-center mb-8"
              variants={itemVariants}
            >
              <motion.div
                className="mb-4"
                variants={itemVariants}
              >
                <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-gray-200/10 text-white">
                  Confirm Your Information
                </span>
              </motion.div>
              <motion.h1
                className="text-3xl lg:text-4xl font-medium text-white mb-3 tracking-tight"
                variants={itemVariants}
              >
                Confirm Email
              </motion.h1>
              <motion.p
                className="text-gray-400 leading-relaxed"
                variants={itemVariants}
              >
                We've sent a confirmation code to <span className="text-white font-medium">{guestData.email}</span>
              </motion.p>
            </motion.div>

            {/* Confirmation Number Input */}
            <motion.div
              variants={itemVariants}
              className="mb-6"
            >
              <FormInput
                label="Confirmation Number"
                type="text"
                value={confirmationNumber}
                onChange={handleConfirmationNumberChange}
                error={confirmationError}
                placeholder="123456"
                maxLength={6}
                isConfirmationCode
                helpText="Enter the confirmation number (use any 4-6 digit number for testing)"
              />
            </motion.div>

            {/* Guest Information */}
            <motion.div
              variants={itemVariants}
              className="space-y-4 mb-8"
            >
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Email Address</label>
                <p className="text-white font-medium">{guestData.email}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">First Name</label>
                  <p className="text-white font-medium">{guestData.firstName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Last Name</label>
                  <p className="text-white font-medium">{guestData.lastName}</p>
                </div>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col gap-3"
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleConfirm}
                disabled={isProcessing}
                className="w-full px-6 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors duration-200 shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  'Confirm and Continue to Payment'
                )}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                disabled={isProcessing}
                className="w-full px-6 py-3.5 bg-gray-800/80 hover:bg-gray-800 text-gray-200 rounded-xl font-medium transition-colors duration-200 border border-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Go Back
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </AnimatePresence>

      {/* Error Modal */}
      <GuestCheckoutErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        errorMessage={error || undefined}
      />
    </>
  );
};

